import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiPost } from "../lib/api";
import { useLogout, useMe } from "./useAuth";

type Props = {
  appName: string;
  appTitle?: string;
  /** Wenn gesetzt: weitere Sektionen unterhalb der Account/Passwort/2FA-Cards */
  extraSections?: React.ReactNode;
};

/**
 * Standard-Profil-Card mit Konto-Info, Passwort-Ändern, 2FA-Einrichtung, Logout.
 * Jede App kann via extraSections eigene Profil-Felder anhängen.
 */
export function ProfileCard({ appName, appTitle, extraSections }: Props) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data: me } = useMe(appName);
  const logout = useLogout(appName);
  const [totpSetup, setTotpSetup] = useState<{ secret: string; otpauth_url: string } | null>(null);
  const [code, setCode] = useState("");
  const [msg, setMsg] = useState("");

  const setupMut = useMutation({
    mutationFn: () => apiPost<{ secret: string; otpauth_url: string }>(`/api/${appName}/auth/totp/setup`),
    onSuccess: (data) => setTotpSetup(data),
    onError: () => setMsg("Konnte TOTP-Setup nicht starten."),
  });
  const confirmMut = useMutation({
    mutationFn: () => apiPost(`/api/${appName}/auth/totp/confirm`, { code }),
    onSuccess: async () => {
      setMsg("2-Faktor aktiviert.");
      setTotpSetup(null); setCode("");
      await qc.invalidateQueries({ queryKey: ["me", appName] });
    },
    onError: () => setMsg("Code falsch — bitte aktuellen Code eingeben."),
  });

  if (!me) return <div className="boot">Lade…</div>;

  return (
    <div className="m-app">
      <div style={{ padding: "16px 16px 110px" }}>
        <h2 style={{ fontSize: 22, fontWeight: 700, color: "var(--m-ink)", margin: "8px 0 16px" }}>
          {appTitle ?? "Konto"}
        </h2>

        <section className="profile-card">
          <h3 className="profile-h">Account</h3>
          <div className="profile-row"><span>Email</span><b>{me.email}</b></div>
          {me.full_name ? <div className="profile-row"><span>Name</span><b>{me.full_name}</b></div> : null}
          <div className="profile-row"><span>Session bis</span><b>{new Date(me.expires_at).toLocaleString("de-DE")}</b></div>
        </section>

        <section className="profile-card">
          <h3 className="profile-h">Passwort</h3>
          <button className="login-btn" onClick={() => navigate("/change-password")}>Passwort ändern</button>
        </section>

        <section className="profile-card">
          <h3 className="profile-h">Zwei-Faktor-Authentifizierung</h3>
          {me.totp_enabled ? (
            <div className="profile-row"><span>Status</span><b style={{ color: "var(--m-green)" }}>aktiv</b></div>
          ) : totpSetup ? (
            <>
              <div style={{ fontSize: 13, color: "var(--m-ink-2)", marginBottom: 8 }}>
                <b>Schritt 1:</b> Authenticator-App öffnen, Code als Secret eingeben:
              </div>
              <div className="profile-secret">{totpSetup.secret}</div>
              <div style={{ fontSize: 13, color: "var(--m-ink-2)", margin: "14px 0 8px" }}>
                <b>Schritt 2:</b> Aktuellen 6-stelligen Code eintippen:
              </div>
              <input
                type="text" inputMode="numeric"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder="123456"
                style={{ width: "100%", padding: 12, borderRadius: 10, background: "var(--m-bg-3)", color: "var(--m-ink)", border: "1px solid var(--m-line)", fontSize: 18, textAlign: "center", letterSpacing: "0.3em" }}
              />
              <div style={{ display: "flex", gap: 10, marginTop: 10 }}>
                <button className="login-btn" onClick={() => confirmMut.mutate()} disabled={code.length !== 6 || confirmMut.isPending}>
                  {confirmMut.isPending ? "Prüfe…" : "Aktivieren"}
                </button>
                <button className="login-link" onClick={() => { setTotpSetup(null); setCode(""); }}>Abbrechen</button>
              </div>
            </>
          ) : (
            <>
              <div style={{ fontSize: 13, color: "var(--m-ink-3)", marginBottom: 10, lineHeight: 1.5 }}>
                Empfohlen für zusätzlichen Schutz.
              </div>
              <button className="login-btn" onClick={() => setupMut.mutate()} disabled={setupMut.isPending}>
                {setupMut.isPending ? "Starte…" : "2-Faktor einrichten"}
              </button>
            </>
          )}
          {msg ? <div style={{ marginTop: 10, fontSize: 13, color: "var(--m-ink-2)" }}>{msg}</div> : null}
        </section>

        {extraSections}

        <section className="profile-card">
          <button className="login-btn" style={{ background: "var(--m-bg-3)", color: "var(--m-ink)", border: "1px solid var(--m-line)" }}
            onClick={() => logout.mutate()}>
            Abmelden
          </button>
        </section>
      </div>
    </div>
  );
}
